'use client';

import { c as _c } from "react/compiler-runtime";
import FeatureSection from '@/components/feature-section';
import Footer from '@/components/landing-footer-demo';
import Navigation from '@/components/landing-navigation';
import { Markdown } from '@/components/markdown';
import PrivacyIndex from '@/components/privacy-index';
import scrollTo from '@/lib/utils/scrollTo';
import { ChevronUp } from 'lucide-react';
import { useEffect, useState } from 'react';
type LegalDocumentPageProps = Readonly<{
  description: string;
  documentPath: string;
  showLoadError?: boolean;
  title: string;
}>;
export function LegalDocumentPage(t0) {
  const $ = _c(31);
  const {
    description,
    documentPath,
    showLoadError: t1,
    title
  } = t0;
  const showLoadError = t1 === undefined ? true : t1;
  const [content, setContent] = useState("");
  const [loadError, setLoadError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  let t2;
  if ($[0] !== documentPath || $[1] !== showLoadError) {
    t2 = () => {
      let cancelled = false;
      setLoadError(false);
      fetch(documentPath).then(response => {
        if (showLoadError && !response.ok) {
          throw new Error("Legal document request failed");
        }
        return response.text();
      }).then(text => {
        if (!cancelled) {
          setContent(text);
        }
      }).catch(() => {
        if (!cancelled && showLoadError) {
          setContent("");
          setLoadError(true);
        }
      });
      return () => {
        cancelled = true;
      };
    };
    $[0] = documentPath;
    $[1] = showLoadError;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== documentPath || $[4] !== retryCount || $[5] !== showLoadError) {
    t3 = [documentPath, retryCount, showLoadError];
    $[3] = documentPath;
    $[4] = retryCount;
    $[5] = showLoadError;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setContent("");
      setRetryCount(_temp);
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleRetry = t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Navigation />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="inline-flex mb-5 items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-foreground animate-appear gap-2"><div className="flex items-center gap-2"><span className="text-muted-foreground">Termos e Políticas</span></div></div>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== title) {
    t7 = <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-primary max-w-6xl">{title}</h1>;
    $[10] = title;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== description) {
    t8 = <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl leading-relaxed">{description}</p>;
    $[12] = description;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t7 || $[15] !== t8) {
    t9 = <div className="container mx-auto mt-48"><div className="flex flex-col items-center gap-5 justify-center text-center">{t6}{t7}{t8}</div></div>;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <FeatureSection />;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t9) {
    t11 = <div data-legal-webview-hidden={true}>{t9}{t10}</div>;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== content) {
    t12 = <div data-legal-webview-hidden={true}><PrivacyIndex markdownContent={content} /></div>;
    $[20] = content;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="relative min-h-full mt-6 w-px bg-muted"><div className="sticky cursor-pointer top-[55px]"><button type="button" aria-label="Voltar ao topo" onClick={_temp2} className="absolute left-[-11px] top-[40px] size-6 border-0 p-0 xs:bg-indigo-50 bg-green-500 rounded-full shadow-md"><div className="size-full rounded-full flex items-center justify-center"><ChevronUp className="size-4" /></div></button></div></div>;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== content || $[24] !== loadError) {
    t14 = <div className="sm:max-w-[800px] max-w-[90%] flex mx-auto sm:px-6 lg:px-8 mb-24"><div className="flex gap-6 relative">{t13}{loadError ? <div className="flex flex-col items-center justify-center gap-4 py-24 text-center flex-1"><p role="alert" className="text-lg text-muted-foreground">Não foi possível carregar este documento.</p><button type="button" onClick={handleRetry} className="rounded-full bg-green-500 px-5 py-2 font-semibold text-black">Tentar novamente</button></div> : <Markdown className="flex-col">{content}</Markdown>}</div></div>;
    $[23] = content;
    $[24] = loadError;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="md:grid md:place-items-center"><section id="footer" className="bg-black mx-auto text-white"><Footer /></section></div>;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t11 || $[28] !== t12 || $[29] !== t14) {
    t16 = <div>{t5}<div className="flex-col flex gap-24">{t11}{t12}{t14}{t15}</div></div>;
    $[27] = t11;
    $[28] = t12;
    $[29] = t14;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  return t16;
}
function _temp2() {
  return scrollTo();
}
function _temp(count) {
  return count + 1;
}